import { useCallback } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { isFiniteNumber } from '@core';
import {
    activeSegmentationsMapAtom,
    activeSwingIdAtom,
    MAX_PROGRESS,
    pendingScrubberLockAtom,
    progressAtom,
    scrubberLockAtom,
    totalFramesAtom,
} from '@store';

const FIRST_POSITION = 'P1';

const getClosestPosition = (
    segmentationMap: Record<string, number> | undefined,
    progress: number,
    totalFrames: number,
) => {
    const segmentationEntries = Object.entries(segmentationMap ?? {})
        .filter(([, frame]) => isFiniteNumber(frame))
        .sort(([, a], [, b]) => a - b);

    if (!segmentationEntries.length || totalFrames <= 0) {
        return FIRST_POSITION;
    }

    return segmentationEntries.reduce(
        (best, [position, frame]) => {
            const positionProgress = (frame / totalFrames) * MAX_PROGRESS;
            const distance = Math.abs(positionProgress - progress);
            return distance < best.distance ? { position, distance } : best;
        },
        { position: FIRST_POSITION, distance: Number.POSITIVE_INFINITY },
    ).position;
};

export const useScrubberLock = (onSelectActive: (swingId: string) => void) => {
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const activeSegmentationsMap = useAtomValue(activeSegmentationsMapAtom);
    const totalFrames = useAtomValue(totalFramesAtom);
    const progress = useAtomValue(progressAtom);
    const scrubberLock = useAtomValue(scrubberLockAtom);

    const setProgress = useSetAtom(progressAtom);
    const setPendingScrubberLock = useSetAtom(pendingScrubberLockAtom);

    const handleSelectActive = useCallback(
        (swingId: string) => {
            if (!swingId || swingId === activeSwingId) {
                return;
            }

            if (!scrubberLock) {
                setPendingScrubberLock(null);
                setProgress(0);
                onSelectActive(swingId);
                return;
            }

            if (!activeSwingId) {
                return;
            }

            setPendingScrubberLock({
                position: getClosestPosition(activeSegmentationsMap, progress, totalFrames),
                sourceSwingId: activeSwingId,
            });
            onSelectActive(swingId);
        },
        [
            activeSegmentationsMap,
            activeSwingId,
            onSelectActive,
            progress,
            scrubberLock,
            setPendingScrubberLock,
            setProgress,
            totalFrames,
        ],
    );

    return handleSelectActive;
};
